import _ from 'underscore';
import { FilterStorageFactory } from '@bingads-webui-campaign/filter-storage';
import {
  saveGeneralInsightsPreferences,
  FEATURE_NAME_GENERAL_INSIGHTS,
  FEATURE_NAME_GENERAL_INSIGHTS_SHOPPING,
  CAMPAIGN_TYPES,
} from '@bingads-webui-campaign/competition-auction-insight-preferences';

function getRouteforScope(scope, prefix = '') {
  let suffix;

  if (scope.adGroupId) {
    suffix = 'adgroup_competition';
  } else if (scope.campaignId) {
    suffix = 'campaign_competition';
  } else {
    suffix = 'competition';
  }

  return prefix + suffix;
}

function getRoute(scope, isForVNext, pageName) {
  let route;

  if (isForVNext) {
    if (pageName === 'campaigns') {
      route = getRouteforScope(scope, 'campaigns_');
    } else if (pageName === 'adgroups') {
      route = getRouteforScope(scope, 'adgroups_');
    } else if (pageName === 'keywords') {
      route = getRouteforScope(scope, 'keywords_');
    } else {
      // Unknown page, go to competition page
      route = getRouteforScope(scope);
    }
  } else {
    route = getRouteforScope(scope);
  }

  return route;
}

function getEntityType(item) {
  if (item.AdGroup) {
    return 'Keyword';
  }
  if (item.Campaign) {
    return 'AdGroup';
  }
  return 'Campaign';
}

const AUCTION_INSIGHT_ENTITY_NAME = 'AuctionInsight';

function getEditableFilter(preferenceService, featureName) {
  const factory = new FilterStorageFactory({
    preferenceService,
    featureName,
  });

  return factory.create('editableFilter', {
    filterEntityOptions: {
      entitySchema: { name: AUCTION_INSIGHT_ENTITY_NAME },
    },
  });
}

function getAuctionInsightEditableFilter(preferenceService, enableBSCAuctionInsights) {
  const storageArr = [getEditableFilter(preferenceService, FEATURE_NAME_GENERAL_INSIGHTS)];

  if (enableBSCAuctionInsights) {
    storageArr.push(getEditableFilter(preferenceService, FEATURE_NAME_GENERAL_INSIGHTS_SHOPPING));
  }
  return storageArr;
}

/**
 * Writes the editable filter for the Auction Insights grid, overwriting any existing filter.
 * This allows the grid to be filtered to only the selected grid rows.
 * @param {Object} preferenceService - Session store used by filter storage.
 * @param {Object} query - Query to be used when the auction insights grid fetches data.
 * @param {bool} enableBSCAuctionInsights - if campaign type dropdown is enabled in auction insights
 * @returns {void}
 */
function writeAuctionInsightEditableFilter(preferenceService, query, enableBSCAuctionInsights) {
  const storageArr = getAuctionInsightEditableFilter(preferenceService, enableBSCAuctionInsights);

  const filter = {
    query,
    name: '',
    id: null,
  };

  _.each(storageArr, (storage) => {
    storage.save(filter);
  });
}

/**
 * Deletes any existing editable filter for the Auction Insights grid.
 * @param {Object} preferenceService - Session store used by filter storage.
 * @param {bool} enableBSCAuctionInsights - if campaign type dropdown is enabled in auction insights
 * @returns {void}
 */
function cleanAuctionInsightEditableFilter(preferenceService, enableBSCAuctionInsights) {
  const storageArr = getAuctionInsightEditableFilter(preferenceService, enableBSCAuctionInsights);

  _.each(storageArr, (storage) => {
    storage.clean();
  });
}

/**
 * Navigates to the Auction Insights section of the Competition Tab.
 * @param {Object} pluginConfig - Plugin config.
 * @param {Object[]} [selectedItems=null] - List of grid rows that are currently selected.
 * @returns {void}
 */
export function navigateToCompetitionTab(pluginConfig, selectedItems = null) {
  const {
    scope, legacyRouter, preferenceService, permissions, isForVNext, pageName,
  } = pluginConfig;
  const enableBSCAuctionInsights = permissions.IsBAACompetitionBSCAuctionInsightsEnabled;

  if (_.isArray(selectedItems) && selectedItems.length > 0) {
    const entityType = getEntityType(selectedItems[0]);

    // $contains is an array of strings that specifies the id
    const $contains = _.map(selectedItems, (item) => {
      // convert each item into a basic object for filter
      const filterObject = _.pick(item, 'Id', 'Name');

      // special logic to handle new keyword
      if (_.isUndefined(filterObject.Name)) {
        filterObject.Name = item.Text;
      } else {
        if (item.Campaign) {
          filterObject.CampaignName = item.Campaign.Name;
          filterObject.CampaignId = item.Campaign.Id;
        }
        if (item.AdGroup) {
          filterObject.AdGroupName = item.AdGroup.Name;
          filterObject.AdGroupId = item.AdGroup.Id;
        }
      }

      // filter $contains expects a string
      return JSON.stringify(filterObject);
    });

    const query = {
      [entityType]: { $contains },
    };

    // write stringified version to session storage
    writeAuctionInsightEditableFilter(preferenceService, query, enableBSCAuctionInsights);

    if (enableBSCAuctionInsights) {
      if (_.find(selectedItems, (item) => {
        if (item.Campaign) {
          return item.Campaign.CampaignType !== CAMPAIGN_TYPES.Shopping;
        }
        return item.CampaignType !== CAMPAIGN_TYPES.Shopping;
      })) {
        saveGeneralInsightsPreferences(preferenceService, { selectShoppingCampaignType: false });
      } else {
        saveGeneralInsightsPreferences(preferenceService, { selectShoppingCampaignType: true });
      }
    }
  } else {
    // remove any existing filter so that all entities are displayed in auction insights grid
    cleanAuctionInsightEditableFilter(preferenceService, enableBSCAuctionInsights);
  }

  const route = getRoute(scope, isForVNext, pageName);

  if (isForVNext) {
    legacyRouter.navigateToRoute(route, {
      campaign_id: scope.campaignId,
      adgroup_id: scope.adGroupId,
    });
  } else {
    legacyRouter.navigateToRoute(route);
  }
}
